// apps/client/src/input/input.ts — keyboard + floating joystick + HUD buttons + taps, as one source the input system reads (§8.3)
import { tuning } from '@whiskerton/content';
import { FloatingJoystick } from './joystick';
import { keyButton, keyVector, type Button } from './keys';

const typing = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
};

export class Input {
  private readonly target: HTMLElement;
  private readonly joystick: FloatingJoystick;
  private readonly keys = new Set<string>();
  private readonly buttons = new Map<Button, Set<() => void>>();
  private readonly taps = new Set<(ndc: { x: number; y: number }) => void>();
  private readonly downs = new Map<number, { x: number; y: number; t: number }>();
  private bHeld = false;
  constructor(target: HTMLElement) {
    this.target = target;
    this.joystick = new FloatingJoystick(target);
    window.addEventListener('keydown', this.keydown);
    window.addEventListener('keyup', this.keyup);
    window.addEventListener('blur', this.blur);
    target.addEventListener('pointerdown', this.pointerdown);
    target.addEventListener('pointerup', this.pointerup);
    target.addEventListener('pointercancel', this.pointercancel);
  }
  /** Screen-space move vector (x right, y down), length ≤ 1. The joystick wins while a thumb is on it. */
  vector(): { x: number; y: number; running: boolean } {
    const shift = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    if (this.joystick.active) { const j = this.joystick.vector(); return { x: j.x, y: j.y, running: j.running || shift || this.bHeld }; }
    const k = keyVector(this.keys);
    return { x: k.x, y: k.y, running: k.running || this.bHeld };
  }
  on(button: Button, fn: () => void): () => void {
    let set = this.buttons.get(button);
    if (!set) { set = new Set(); this.buttons.set(button, set); }
    set.add(fn);
    return () => { set.delete(fn); };
  }
  press(button: Button): void { for (const fn of [...(this.buttons.get(button) ?? [])]) fn(); }
  /** The HUD's B button: held = run. */
  setHeld(_button: 'B', held: boolean): void { this.bHeld = held; }
  onTap(fn: (ndc: { x: number; y: number }) => void): () => void {
    this.taps.add(fn);
    return () => { this.taps.delete(fn); };
  }
  dispose(): void {
    window.removeEventListener('keydown', this.keydown);
    window.removeEventListener('keyup', this.keyup);
    window.removeEventListener('blur', this.blur);
    this.target.removeEventListener('pointerdown', this.pointerdown);
    this.target.removeEventListener('pointerup', this.pointerup);
    this.target.removeEventListener('pointercancel', this.pointercancel);
    this.joystick.dispose();
    this.keys.clear(); this.buttons.clear(); this.taps.clear(); this.downs.clear();
  }
  private readonly keydown = (e: KeyboardEvent) => {
    if (typing(e.target)) return;                                   // typing chat never walks
    const b = keyButton(e.code);
    // A focused HUD button already handles its own Space/Enter click.
    if (b === 'A' && e.code !== 'KeyE' && (e.target as HTMLElement | null)?.closest?.('button')) return;
    this.keys.add(e.code);
    if (b && !e.repeat) this.press(b);
  };
  private readonly keyup = (e: KeyboardEvent) => { this.keys.delete(e.code); };
  private readonly blur = () => { this.keys.clear(); this.bHeld = false; };
  private readonly pointerdown = (e: PointerEvent) => { this.downs.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() }); };
  private readonly pointercancel = (e: PointerEvent) => { this.downs.delete(e.pointerId); };
  /** A tap = released within tapMaxMs and tapMaxPx of its press (contract change 32), not on HUD (.hit). */
  private readonly pointerup = (e: PointerEvent) => {
    const d = this.downs.get(e.pointerId);
    this.downs.delete(e.pointerId);
    if (!d || this.taps.size === 0) return;
    if (performance.now() - d.t > tuning.input.tapMaxMs || Math.hypot(e.clientX - d.x, e.clientY - d.y) > tuning.input.tapMaxPx) return;
    if ((e.target as HTMLElement | null)?.closest?.('.hit')) return;
    // A quick, short press is a tap even in the joystick zone (§8.4: tapping the world walks there); the stick it opened closes on this
    // same release (its window listener runs after this one) and barely moved, so it never fights the walk.
    const ndc = { x: (e.clientX / innerWidth) * 2 - 1, y: -((e.clientY / innerHeight) * 2 - 1) };
    for (const fn of [...this.taps]) fn(ndc);
  };
}
